import React from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import DropDownSvg from '../../assets/svg/ic_arrow_drop_down_24px.svg';
import styles from './DropdownMenu.module.css';
import OptionList, { selectionItemShape } from './OptionList';
import menuContentTransitionStyles from './MenuContents.Transition.module.css';
import menuSelectionTransitionStyles from './MenuSelection.Transition.module.css';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useAnalytics } from 'hooks/useAnalytics';
import RLPError from '../ErrorPage/RLPError';

const propTypes = {
  containingTab: PropTypes.string.isRequired,
  identifier: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string,
  value: PropTypes.string.isRequired,
  previousValue: PropTypes.string.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  selectionItems: PropTypes.arrayOf(selectionItemShape).isRequired,
  onSortSelection: PropTypes.func.isRequired,
  sortExpandStateFn: PropTypes.func.isRequired,
  classes: PropTypes.string,
};

function DropdownMenu(props) {
  const {
    containingTab,
    identifier,
    ariaLabel,
    value,
    previousValue,
    isExpanded,
    selectionItems,
    onSortSelection,
    sortExpandStateFn,
    sortingDropdownRef,
    classes,
  } = props;

  const getSelectedItemDisplayText = () => {
    const selectedItem = selectionItems.find((item) => item.value === value);
    return selectedItem && selectedItem.displayText ? selectedItem.displayText : null;
  };
  const selectedItemDisplayText = getSelectedItemDisplayText();

  useAnalytics(
    `rlp-${containingTab}-dropdown-menu`,
    isExpanded ? 'open-widget' : 'close-widget',
    identifier,
    isExpanded
  );
  useAnalytics(
    `rlp-${containingTab}-dropdown-menu`,
    selectedItemDisplayText ? selectedItemDisplayText.toLowerCase() : 'no-selection',
    identifier,
    value
  );

  const handleCollapse = (event) => {
    try {
      event.preventDefault();
      onSortSelection(false, value);
    } catch (err) {
      // create an RLP error so that the error condition is logged to Datadog
      // also log the error to the console
      new RLPError({ payload: props }, err).log();
    }
  };

  const handleToggleExpansion = (event) => {
    try {
      event.preventDefault();
      onSortSelection(!isExpanded, value);
    } catch (err) {
      // create an RLP error so that the error condition is logged to Datadog
      // also log the error to the console
      new RLPError({ payload: props }, err).log();
    }
  };

  const handleExpansionKeyPress = (event) => {
    if (event.key.toLowerCase().indexOf(' ') === 0 || event.key.toLowerCase().indexOf('enter') === 0) {
      handleToggleExpansion(event);
    } else if (event.key.toLowerCase() !== 'tab' && event.key.toLowerCase().indexOf('esc') === -1) {
      event.preventDefault();
    }
  };

  const [node] = useClickOutside(isExpanded, handleCollapse, handleExpansionKeyPress);

  const OptionListWithForwardRef = React.forwardRef((props, ref) => <OptionList {...props} expandCollapseRef={ref} />);

  const selectionList = (
    <CSSTransition
      in={isExpanded}
      appear={true}
      unmountOnExit={true}
      classNames={menuContentTransitionStyles}
      timeout={menuContentTransitionStyles.transitionTime * 1000}
    >
      <OptionListWithForwardRef
        closeListFn={handleCollapse}
        identifier={identifier}
        isExpanded={isExpanded}
        selectedValue={value}
        selectionItems={selectionItems}
        sortSelectionFn={onSortSelection}
        sortExpandStateFn={sortExpandStateFn}
        ref={sortingDropdownRef}
      />
    </CSSTransition>
  );

  const ariaLabelledBy = ariaLabel ? null : `${identifier}-title ${identifier}-selection`;
  const dropdownClasses = isExpanded
    ? `${styles['expanded']} ${styles['selection-container']} ${classes}`
    : `${styles['selection-container']} ${classes}`;

  return (
    <div className={dropdownClasses} ref={node}>
      <div className={styles['selection']} onClick={handleToggleExpansion}>
        <CSSTransition
          in={!isExpanded && previousValue !== value}
          appear={true}
          classNames={menuSelectionTransitionStyles}
          timeout={menuSelectionTransitionStyles.transitionTime * 1000}
        >
          <span id={`${identifier}-selection`} className={styles['selection-display']}>
            {selectedItemDisplayText}
          </span>
        </CSSTransition>

        <span
          role="button"
          aria-labelledby={ariaLabelledBy}
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={isExpanded}
          tabIndex={0}
          className={`${styles['icon']} ${styles['icon-arrow']} ${
            isExpanded ? styles['icon-up'] : styles['icon-down']
          }`}
          onKeyDown={handleExpansionKeyPress}
          onClick={handleToggleExpansion}
          ref={sortingDropdownRef}
        >
          <DropDownSvg key="collapsed" />
        </span>
      </div>
      {selectionList}
    </div>
  );
}

DropdownMenu.defaultProps = {
  classes: '',
};

DropdownMenu.propTypes = propTypes;

export default DropdownMenu;
